'use client';

import { useFeatureFlagsContext } from '@/@feature-flags/context';
import { FeatureGate } from '@/@feature-flags/components/FeatureGate';
import {
    FEATURE_FLAG_QA_VERIFICATION_KEYS,
    isFeatureFlagsQaDashboardEnabled,
} from '@/@feature-flags/config';
import { featureFlagService } from '@/@feature-flags/services';
import { Routes } from '@/app/routes';
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Chip,
    Link,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { StaffPage } from '@/components/staff-tools/staff-page';
import { STAFF_TABLE } from '@/components/staff-tools/staff-table';
import { useRouter } from 'next/navigation';
import { useCallback, useMemo, useState } from 'react';

const QA_KEYS = [...FEATURE_FLAG_QA_VERIFICATION_KEYS];

export function FeatureFlagsQaDashboard() {
    const router = useRouter();
    const {
        flags: frontendFlags,
        isLoading: isFrontendLoading,
        refetch,
    } = useFeatureFlagsContext();

    const [backendFlags, setBackendFlags] = useState<Record<string, boolean | undefined>>({});
    const [backendError, setBackendError] = useState<string | null>(null);
    const [checkingAll, setCheckingAll] = useState(false);
    const [checkingKey, setCheckingKey] = useState<string | null>(null);

    const runBackendCheck = useCallback(
        async (keys: string[]) => {
            setBackendError(null);
            const data = await featureFlagService.fetchAccessLive(keys);
            setBackendFlags((prev) => {
                const next = { ...prev };
                for (const key of keys) {
                    next[key] = data.flags[key] ?? false;
                }
                return next;
            });
            featureFlagService.clearCache();
            refetch();
        },
        [refetch],
    );

    const handleCheckAll = useCallback(async () => {
        setCheckingAll(true);
        try {
            await runBackendCheck(QA_KEYS);
        } catch (e) {
            setBackendError(e instanceof Error ? e.message : 'Backend check failed');
        } finally {
            setCheckingAll(false);
        }
    }, [runBackendCheck]);

    const handleCheckOne = useCallback(
        async (key: string) => {
            setCheckingKey(key);
            try {
                await runBackendCheck([key]);
            } catch (e) {
                setBackendError(e instanceof Error ? e.message : 'Backend check failed');
            } finally {
                setCheckingKey(null);
            }
        },
        [runBackendCheck],
    );

    const rows = useMemo(
        () =>
            QA_KEYS.map((key) => {
                const frontend = isFrontendLoading ? null : Boolean(frontendFlags[key]);
                const backend = backendFlags[key];
                const mismatch = backend !== undefined && frontend !== null && frontend !== backend;

                return { key, frontend, backend, mismatch };
            }),
        [frontendFlags, backendFlags, isFrontendLoading],
    );

    if (!isFeatureFlagsQaDashboardEnabled()) {
        return null;
    }

    return (
        <StaffPage
            width="wide"
            actions={
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<Icon icon="lucide:arrow-left" className="h-4 w-4" aria-hidden />}
                    onPress={() => router.push(Routes.FEATURE_FLAGS)}
                >
                    Back to Feature Flags
                </Button>
            }
            description={
                <>
                    Verifies that the three QA demo keys are loaded into the app provider (what{' '}
                    <code className="rounded bg-default-100 px-1 py-0.5 text-tiny">
                        FeatureGate
                    </code>{' '}
                    uses) and matches a direct{' '}
                    <code className="rounded bg-default-100 px-1 py-0.5 text-tiny">
                        GET /feature-flags/access
                    </code>{' '}
                    call. Create the keys in{' '}
                    <Link href={Routes.FEATURE_FLAGS} size="sm">
                        Feature Flags admin
                    </Link>{' '}
                    if they do not exist yet.
                </>
            }
            below={
                <p className="text-tiny text-foreground-600">
                    Keys: <span className="font-mono">{QA_KEYS.join(', ')}</span>
                </p>
            }
        >
            <Card shadow="sm" className="rounded-large">
                <CardHeader className="flex flex-col gap-3 border-b border-default-200/50 pb-4 dark:border-default-100/10 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h2 className="text-tiny font-semibold uppercase tracking-wide text-foreground-600">
                            Provider vs backend
                        </h2>
                        <p className="text-tiny text-foreground-600">
                            Frontend column reflects the FeatureFlags provider; use the buttons to
                            query the portal API again (cache cleared first).
                        </p>
                    </div>
                    <Button
                        color="primary"
                        size="sm"
                        isLoading={checkingAll}
                        onPress={() => void handleCheckAll()}
                        startContent={
                            checkingAll ? null : (
                                <Icon icon="lucide:refresh-cw" className="h-4 w-4" />
                            )
                        }
                    >
                        Check all on backend
                    </Button>
                </CardHeader>
                <CardBody className="gap-3">
                    {backendError ? (
                        <div className="flex items-center gap-2 rounded-medium bg-danger-50 px-3 py-2 text-small text-danger dark:bg-danger-900/30">
                            <Icon icon="lucide:alert-circle" className="h-4 w-4 shrink-0" />
                            {backendError}
                        </div>
                    ) : null}

                    <Table
                        aria-label="QA feature flag status"
                        removeWrapper
                        classNames={{ th: STAFF_TABLE.th, td: STAFF_TABLE.td }}
                    >
                        <TableHeader>
                            <TableColumn>Key</TableColumn>
                            <TableColumn>Frontend (provider)</TableColumn>
                            <TableColumn>Backend (last check)</TableColumn>
                            <TableColumn align="end">
                                <span className="sr-only">Actions</span>
                            </TableColumn>
                        </TableHeader>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={row.key}>
                                    <TableCell>
                                        <span className="font-mono text-tiny">{row.key}</span>
                                        {row.mismatch ? (
                                            <Chip
                                                size="sm"
                                                color="warning"
                                                variant="flat"
                                                className="ml-2"
                                            >
                                                Mismatch
                                            </Chip>
                                        ) : null}
                                    </TableCell>
                                    <TableCell>
                                        {isFrontendLoading ? (
                                            <Spinner size="sm" />
                                        ) : row.frontend ? (
                                            <Chip size="sm" color="success" variant="flat">
                                                Granted
                                            </Chip>
                                        ) : (
                                            <Chip size="sm" color="danger" variant="flat">
                                                Denied
                                            </Chip>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {row.backend === undefined ? (
                                            <span className="text-tiny text-foreground-600">
                                                Not checked
                                            </span>
                                        ) : row.backend ? (
                                            <Chip size="sm" color="success" variant="flat">
                                                Granted
                                            </Chip>
                                        ) : (
                                            <Chip size="sm" color="danger" variant="flat">
                                                Denied
                                            </Chip>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex justify-end">
                                            <Button
                                                size="sm"
                                                variant="bordered"
                                                isLoading={checkingKey === row.key}
                                                onPress={() => void handleCheckOne(row.key)}
                                            >
                                                Check backend
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardBody>
            </Card>

            <div>
                <h2 className="mb-1 text-tiny font-semibold uppercase tracking-wide text-foreground-600">
                    FeatureGate samples
                </h2>
                <p className="mb-4 text-small text-foreground-600">
                    Each card shows content only when the flag is enabled for the signed-in user—the
                    same gating used in the product.
                </p>
                <div className="grid gap-4 md:grid-cols-3">
                    {QA_KEYS.map((key, index) => (
                        <Card key={key} shadow="sm" className="rounded-large">
                            <CardHeader className="flex flex-col items-start gap-1 border-b border-default-200/50 pb-3 dark:border-default-100/10">
                                <span className="text-tiny font-medium text-foreground-600">
                                    Demo {index + 1}
                                </span>
                                <span className="font-mono text-small text-foreground">{key}</span>
                            </CardHeader>
                            <CardBody>
                                <FeatureGate
                                    flag={key}
                                    fallback={
                                        <div className="dark:bg-danger-950/40 rounded-medium border border-dashed border-danger-200 bg-danger-50/80 px-3 py-4 text-center text-tiny text-danger-700 dark:border-danger-900 dark:text-danger-300">
                                            <Icon
                                                icon="lucide:lock"
                                                className="mx-auto mb-2 h-8 w-8 opacity-80"
                                            />
                                            Gated off — flag is disabled or missing for this user.
                                        </div>
                                    }
                                >
                                    <div className="dark:bg-success-950/40 rounded-medium border border-success-200 bg-success-50/80 px-3 py-4 text-center text-tiny text-success-800 dark:border-success-900 dark:text-success-200">
                                        <Icon
                                            icon="lucide:check-circle"
                                            className="mx-auto mb-2 h-8 w-8 opacity-90"
                                        />
                                        Gated content visible — flag is on.
                                    </div>
                                </FeatureGate>
                            </CardBody>
                        </Card>
                    ))}
                </div>
            </div>
        </StaffPage>
    );
}
